import { textLevel, textLevelOf, lineEndingOf, pastedLineEnding } from '../../utils/monacoLargeText';
import { isWindows } from '../../utils/nativePrint';

/**
 * The text of a CodeEditor's Monaco model <-> its `value` / `onChange` props.
 *
 * Replaces @monaco-editor/react's own value/onChange wiring, which on every
 * keystroke read the whole model twice — once for onChange, once more when the
 * echoed `value` came back, to compare it with the model. At 9.5 MB that is two
 * 10 MB strings built and one 10 MB comparison per character typed. Here the
 * model is read once per edit, and an echo is recognised by identity.
 *
 * Line endings: an empty model takes LF (Monaco's Windows default is CRLF, so
 * every pasted LF body was sent as CRLF); a paste that replaces the whole text
 * keeps the line ending it brings — except on Windows, whose clipboard brings
 * CRLF for everything (see pastedLineEnding).
 *
 * @param {object} editor  Monaco editor
 * @param {object} monaco  Monaco namespace
 * @param {{
 *   onChange: (value: string) => void,
 *   onLargeChange: (level: 0|1, chars: number) => void,   // normal / large (utils/monacoLargeText);
 *                                              // also called BEFORE a paste or replace grows it
 *   onEdit?: () => void,
 *   windowsClipboard?: boolean,   // the clipboard turns \n into \r\n (utils/monacoLargeText)
 * }} handlers
 */
export function bindEditorText(editor, monaco, { onChange, onLargeChange, onEdit, windowsClipboard = isWindows() }) {
  // Unknown until the first applyExternal — which the caller makes right away,
  // with the value as it is NOW (the model was made from an earlier render's).
  let lastEmitted;
  let applying = false; // our own model writes must not echo back as user edits
  let level = null;
  const model0 = editor.getModel();
  const { LF, CRLF } = monaco.editor.EndOfLineSequence;

  const silently = (fn) => {
    applying = true;
    try { fn(); } finally { applying = false; }
  };

  const checkLarge = () => {
    const model = editor.getModel();
    if (!model) return;
    const next = textLevel(model.getValueLength(), model.getLineCount());
    if (next !== level) {
      level = next;
      onLargeChange(next, model.getValueLength());
    }
  };

  // Large-text options go on BEFORE Monaco takes in a large paste or replace:
  // switched after, Monaco has already wrapped all 500k lines once, and then
  // rebuilds the whole view for the new options.
  const becomingLarge = (resultLength, text) => {
    const next = Math.max(textLevel(resultLength, 0), textLevelOf(text));
    if (next <= (level || 0)) return;
    level = next;
    onLargeChange(next, resultLength); // the size it WILL be — Monaco is about to insert it
  };

  if (model0 && model0.getLineCount() === 1) silently(() => model0.setEOL(LF));
  checkLarge();

  const changeSub = editor.onDidChangeModelContent(() => {
    checkLarge();
    if (!applying) {
      const model = editor.getModel();
      lastEmitted = model ? model.getValue() : '';
      onChange(lastEmitted);
    }
    onEdit?.();
  });

  // Capture phase on WINDOW: Monaco's CopyPasteController catches `paste` in the
  // capture phase on the editor's container and applies it right there, so a
  // listener on the editor itself runs after the text is already in. Ours must
  // run before it — for the line ending, and for the large-text options.
  const container = editor.getContainerDomNode?.() || editor.getDomNode();
  const onPaste = (e) => {
    if (typeof container?.contains !== 'function' || !container.contains(e.target)) return;
    const model = editor.getModel();
    const text = e.clipboardData?.getData('text/plain');
    if (!model || !text) return;
    const selection = editor.getSelection();
    const replacesAll = model.getValueLength() === 0
      || (selection && selection.equalsRange(model.getFullModelRange()));
    // A read-only editor takes no paste. (No re-check on a timer instead: Monaco
    // applies a paste asynchronously, and a timer that fires in between saw the
    // empty model and switched back before the 10 MB went in.)
    if (!editor.getRawOptions?.().readOnly) {
      becomingLarge(replacesAll ? text.length : model.getValueLength() + text.length, text);
    }
    const eol = replacesAll ? pastedLineEnding(text, windowsClipboard) : null;
    if (!eol) return;
    const wanted = eol === 'crlf' ? CRLF : LF;
    if (model.getEOL() !== (eol === 'crlf' ? '\r\n' : '\n')) silently(() => model.setEOL(wanted));
  };
  window.addEventListener('paste', onPaste, true);

  return {
    /** The `value` prop changed — write it into the model unless it is our own echo. */
    applyExternal(value, readOnly) {
      const next = value ?? '';
      if (next === lastEmitted) return;
      lastEmitted = next;
      const model = editor.getModel();
      if (!model) return;
      if (model.getValueLength() === next.length && model.getValue() === next) return;
      becomingLarge(next.length, next);
      silently(() => {
        // The whole text is replaced: take its line ending, as a paste does.
        const eol = lineEndingOf(next);
        if (eol) model.setEOL(eol === 'crlf' ? CRLF : LF);
        if (readOnly) {
          editor.setValue(next);
        } else {
          editor.executeEdits('', [{ range: model.getFullModelRange(), text: next, forceMoveMarkers: true }]);
          editor.pushUndoStop();
        }
      });
      checkLarge();
    },
    dispose() {
      changeSub.dispose();
      window.removeEventListener('paste', onPaste, true);
    },
  };
}
